import { cn } from '@morpho-org/ui-v6/utils'
import { forwardRef, type SVGAttributes } from 'react'

/**
 * Allocation palette colors for donut charts.
 * CSS tokens keep allocation cells in sync with the chart and active theme.
 */
export const ALLOCATION_PALETTE = [
  'var(--series-0)',
  'var(--series-1)',
  'var(--series-2)',
  'var(--series-3)',
  'var(--series-4)',
  'var(--series-5)',
  'var(--series-6)',
  'var(--series-7)',
  'var(--series-8)',
  'var(--series-9)'
] as const

/**
 * Get a color from the allocation palette based on index.
 * Colors cycle through the palette.
 */
export const getDonutColor = (index: number): string => {
  return ALLOCATION_PALETTE[index % ALLOCATION_PALETTE.length] ?? ALLOCATION_PALETTE[0]
}

export interface DonutProps extends Omit<SVGAttributes<SVGSVGElement>, 'children'> {
  /** Allocation percentage (0-100) */
  allocation: number
  /** Color of the filled arc */
  color: string
  /** Color of the background arc */
  backgroundColor: string
  /** Outer radius of the donut */
  radius?: number
  /** Thickness of the donut ring */
  thickness?: number
  /** Radius of the inner hole */
  holeRadius?: number
  /** Offset percentage to rotate the start position (0-100) */
  percentageOffset?: number
}

/**
 * Donut component - Tailwind replica of dapp-ui-v5 Donut
 *
 * A pure SVG donut chart component for displaying allocation percentages.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Donut
 *   allocation={55}
 *   color={getDonutColor(0)}
 *   backgroundColor="#e5e7eb"
 * />
 *
 * // With custom size
 * <Donut
 *   allocation={75}
 *   color="#2470ff"
 *   backgroundColor="#f3f4f6"
 *   radius={20}
 *   thickness={8}
 * />
 * ```
 */
const Donut = forwardRef<SVGSVGElement, DonutProps>(
  (
    {
      allocation,
      color,
      backgroundColor,
      radius = 9,
      thickness = 6,
      holeRadius = 1.5,
      percentageOffset,
      className,
      ...props
    },
    ref
  ) => {
    const innerRadius = radius - thickness / 2
    const circumference = 2 * Math.PI * innerRadius
    const size = radius * 2
    const holeSize = holeRadius * 2

    const cappedAllocation = Math.min(allocation, 100)
    const arcLength = (cappedAllocation / 100) * circumference
    const strokeDasharray = `${arcLength} ${circumference}`

    // Start at 12 o'clock instead of 3 o'clock by subtracting 90 degrees
    const baseOffset = -90
    const rotation = percentageOffset ? (percentageOffset / 100) * 360 : 0
    const degreeRotation = baseOffset + rotation

    return (
      <svg
        ref={ref}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-label={`Donut chart showing ${allocation}% allocation`}
        className={cn('shrink-0', className)}
        {...props}
      >
        {/* Background circle */}
        <circle
          cx={radius}
          cy={radius}
          r={innerRadius}
          fill="none"
          stroke={backgroundColor}
          strokeWidth={thickness}
        />
        {/* Allocation arc */}
        <circle
          cx={radius}
          cy={radius}
          r={innerRadius}
          fill="none"
          stroke={color}
          strokeWidth={thickness}
          strokeDasharray={strokeDasharray}
          transform={`rotate(${degreeRotation} ${radius} ${radius})`}
        />
        {/* Center hole */}
        <circle cx={radius} cy={radius} r={holeSize} fill="transparent" />
      </svg>
    )
  }
)

Donut.displayName = 'Donut'

export { Donut }
